import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/apiClient";
import { useAuth } from "@/context/AuthContext";
import { DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogClose } from "@/components/ui/dialog";
import { GuardedDialog, isDirty, useOpenSnapshot } from "@/components/support/GuardedDialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Plus, MagnifyingGlass } from "@phosphor-icons/react";
import { toast } from "sonner";

const EMPTY = { company_name: "", contact_name: "", email: "", phone: "", notes: "" };

export default function Customers() {
  const { currentOrgId } = useAuth();
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [f, setF] = useState(EMPTY);
  const snap = useOpenSnapshot(open, f);

  const load = () => { if (currentOrgId) api.get(`/support/customers?org_id=${currentOrgId}${q ? `&q=${encodeURIComponent(q)}` : ""}`).then((r) => setRows(r.data)).catch(() => {}); };
  useEffect(load, [currentOrgId, q]);

  const create = async () => {
    if (!f.company_name.trim()) { toast.error("Company name required"); return; }
    try {
      const contacts = f.contact_name ? [{ name: f.contact_name, email: f.email, phone: f.phone, is_primary: true }] : [];
      await api.post("/support/customers", { org_id: currentOrgId, company_name: f.company_name, contacts, notes: f.notes });
      setOpen(false); setF(EMPTY);
      toast.success("Customer created"); load();
    } catch { toast.error("Could not create customer"); }
  };

  return (
    <div className="space-y-4" data-testid="customers-page">
      <div className="flex items-center justify-between gap-3">
        <div className="relative flex-1 max-w-sm">
          <MagnifyingGlass size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
          <Input data-testid="customer-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search customers" className="pl-9 bg-black border-zinc-800" />
        </div>
        <Button data-testid="new-customer-button" onClick={() => setOpen(true)} className="bg-blue-600 hover:bg-blue-500"><Plus size={15} className="mr-1.5" /> New customer</Button>
      </div>
      <div className="rounded-xl border border-white/10 bg-zinc-900/60 divide-y divide-white/5">
        {rows.map((c) => (
          <button key={c.customer_id} data-testid={`customer-row-${c.customer_id}`} onClick={() => navigate(`/helpdesk/customers/${c.customer_id}`)}
            className="w-full text-left flex items-center justify-between px-4 py-3 hover:bg-white/5">
            <div>
              <div className="text-sm font-medium">{c.company_name}</div>
              <div className="text-xs text-zinc-500">{c.status} · {c.open_tickets} open ticket{c.open_tickets === 1 ? "" : "s"}</div>
            </div>
            <span className={`text-[10px] px-1.5 py-0.5 rounded border ${c.status === "active" ? "text-emerald-400 border-emerald-500/30 bg-emerald-500/10" : "text-zinc-400 border-white/10 bg-white/5"}`}>{c.status}</span>
          </button>
        ))}
        {rows.length === 0 && <div className="p-8 text-center text-sm text-zinc-500">No customers yet. Create your first one.</div>}
      </div>

      <GuardedDialog open={open} onOpenChange={setOpen} dirty={isDirty(f, snap)} onDiscard={() => setF(EMPTY)} className="bg-zinc-900 border-white/10" testid="customer-dialog">
          <DialogHeader><DialogTitle className="font-heading tracking-tight">New customer</DialogTitle>
            <DialogDescription className="text-zinc-400 text-sm">Add a company you provide IT support for.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-1">
            <div><Label className="text-xs text-zinc-400">Company name *</Label><Input data-testid="customer-company-input" value={f.company_name} onChange={(e) => setF({ ...f, company_name: e.target.value })} className="mt-1 bg-black border-zinc-800" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs text-zinc-400">Primary contact</Label><Input value={f.contact_name} onChange={(e) => setF({ ...f, contact_name: e.target.value })} className="mt-1 bg-black border-zinc-800" /></div>
              <div><Label className="text-xs text-zinc-400">Email</Label><Input value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} className="mt-1 bg-black border-zinc-800" /></div>
            </div>
            <div><Label className="text-xs text-zinc-400">Phone</Label><Input value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} className="mt-1 bg-black border-zinc-800" /></div>
            <div><Label className="text-xs text-zinc-400">Notes</Label><Textarea value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} rows={2} className="mt-1 bg-black border-zinc-800" /></div>
          </div>
          <DialogFooter>
            <DialogClose asChild><Button variant="ghost" data-testid="customer-cancel-button" className="text-zinc-400">Cancel</Button></DialogClose>
            <Button data-testid="customer-create-submit" onClick={create} className="bg-blue-600 hover:bg-blue-500">Create</Button>
          </DialogFooter>
      </GuardedDialog>
    </div>
  );
}
